import React from "react";
import PropTypes from "prop-types";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Container,
  Paper,
} from "@mui/material";
import { CheckCircle, WarningAmber } from "@mui/icons-material";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import Navbar from "../../../globals/components/Navbar";
import GuestNavbar from "../GuestNavbar";
import PremiumCalculator from "../PremiumCalculatorPublic";
import SupportContactDialog from "../../../components/SupportContactDialog";
import SupportRequestModal from "../../../components/SupportRequestModal";

const GUEST_FOOTER_TAB_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  backgroundColor: "#D3D3D3",
  color: "#333",
  px: 4,
  py: 2,
  borderRadius: "8px 8px 0 0",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#C8C8C8",
    transform: "translateY(-2px)",
    boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
  },
};

const GUEST_FOOTER_ICON_BOX_SX = {
  width: 20,
  height: 20,
  backgroundColor: "#333",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "2px",
  flexShrink: 0,
  "@media (max-width: 1024px)": { width: 18, height: 18 },
  "@media (max-width: 768px)": { width: 16, height: 16 },
};

const GUEST_FOOTER_LABEL_SX = {
  fontSize: "1rem",
  fontWeight: 500,
  color: "#333",
  whiteSpace: "nowrap",
  "@media (max-width: 1024px)": { fontSize: "0.9rem" },
  "@media (max-width: 768px)": { fontSize: "0.8rem" },
};

function BondWizardGuestFooter({ onOpenSupport, onOpenCalculator }) {
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        pr: 2,
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Box
        onClick={onOpenSupport}
        sx={{
          ...GUEST_FOOTER_TAB_SX,
          minWidth: "300px",
          maxWidth: "400px",
          "@media (max-width: 1024px)": {
            minWidth: "260px",
            maxWidth: "340px",
            px: 3,
            py: 1.5,
            gap: 1.5,
          },
          "@media (max-width: 768px)": {
            minWidth: "220px",
            maxWidth: "300px",
            px: 2,
            py: 1,
            gap: 1,
          },
        }}
      >
        <Box sx={GUEST_FOOTER_ICON_BOX_SX}>
          <EmailIcon
            sx={{
              fontSize: 14,
              color: "white",
              "@media (max-width: 1024px)": { fontSize: 12 },
              "@media (max-width: 768px)": { fontSize: 10 },
            }}
          />
        </Box>
        <Typography variant="body1" sx={GUEST_FOOTER_LABEL_SX}>
          Contact Support
        </Typography>
      </Box>

      <Box
        onClick={onOpenCalculator}
        sx={{
          ...GUEST_FOOTER_TAB_SX,
          minWidth: "320px",
          maxWidth: "400px",
          "@media (max-width: 1024px)": {
            minWidth: "280px",
            maxWidth: "350px",
            px: 3,
            py: 1.5,
            gap: 1.5,
          },
          "@media (max-width: 768px)": {
            minWidth: "240px",
            maxWidth: "300px",
            px: 2,
            py: 1,
            gap: 1,
          },
        }}
      >
        <Box sx={GUEST_FOOTER_ICON_BOX_SX}>
          <CalculatorIcon
            sx={{
              fontSize: 14,
              color: "white",
              "@media (max-width: 1024px)": { fontSize: 12 },
              "@media (max-width: 768px)": { fontSize: 10 },
            }}
          />
        </Box>
        <Typography variant="body1" sx={GUEST_FOOTER_LABEL_SX}>
          Use our Bond Premium Calculator
        </Typography>
      </Box>
    </Box>
  );
}

function BondWizardGuestOverlays({
  supportContext,
  openSupport,
  setOpenSupport,
  openCalculator,
  setOpenCalculator,
}) {
  return (
    <>
      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context={supportContext}
      />

      <Dialog
        open={openCalculator}
        onClose={() => setOpenCalculator(false)}
        sx={{ "& .MuiPaper-root": { borderRadius: "20px", maxWidth: "90vw" } }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: "12px",
              color: "#333333",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function BondWizardEsignatureDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Bond Submission</DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" mb={2}>
          <CheckCircle color="success" sx={{ mr: 2 }} />
          <Typography variant="body1">
            The bond was successfully processed.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <WarningAmber color="warning" sx={{ mr: 2 }} />
          <Typography variant="body1">
            The bond will not be forwarded until the indemnity agreement is
            signed.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
        <Button onClick={onClose} color="primary" variant="contained">
          Sign Indemnity Agreement
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BondWizardDuplicateDialog({
  open,
  message,
  duplicateBondId,
  updatePath,
  onCancel,
  onProceed,
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Potential Duplicate Found</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {duplicateBondId ? (
          <Button
            onClick={() =>
              window.open(updatePath(duplicateBondId), "_blank", "noopener,noreferrer")
            }
            color="info"
            variant="outlined"
          >
            View Bond
          </Button>
        ) : null}
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button onClick={onProceed} color="primary" variant="contained">
          Proceed Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function getStandardNextLabel(isLoading, activeStep, stepsLength) {
  if (isLoading) return "Processing...";
  if (activeStep === stepsLength - 1) return "Submit";
  return "Next";
}

function renderBondWizardHeader({ config, HeaderComponent, isGuest, form }) {
  if (config.inlineTitle) {
    return (
      <Box>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          {config.inlineTitle()}
        </Typography>
      </Box>
    );
  }

  if (HeaderComponent) {
    return (
      <HeaderComponent
        isGuest={isGuest}
        onOpenCalculator={() => form.setOpenCalculator(true)}
        onOpenSupport={() => form.setOpenSupport(true)}
      />
    );
  }

  return null;
}

function StandardWizardNextButton({
  activeStep,
  stepsLength,
  isLoading,
  nextDisabled,
  onNext,
  testId,
}) {
  const nextLabel = getStandardNextLabel(isLoading, activeStep, stepsLength);

  return (
    <Button
      variant="contained"
      color="primary"
      {...(testId ? { 'data-testid': testId } : {})}
      onClick={onNext}
      disabled={nextDisabled || isLoading}
      endIcon={isLoading && <CircularProgress size={20} />}
    >
      {nextLabel}
    </Button>
  );
}

function StandardBondWizardLayout({ config, form }) {
  const {
    steps: stepLabels,
    activeStep,
    globalState,
    errors,
    setErrors,
    isLoading,
    isSaving,
    updateGlobalState,
    handleNext,
    handleBack,
    handleStepClick,
    handleSave,
  } = form;

  const isGuest = config.getIsGuest(form);
  const stepContext = {
    ...form,
    globalState,
    errors,
    setErrors,
    updateGlobalState,
    isGuest,
  };
  const activeStepConfig = config.steps[activeStep];
  const nextDisabled = config.isNextDisabled?.(form) ?? false;
  const HeaderComponent = config.HeaderComponent;

  return (
    <Box
      className={config.containerClassName}
      sx={{ pb: isGuest ? 10 : 0 }}
    >
      {renderBondWizardHeader({ config, HeaderComponent, isGuest, form })}

      <Stepper activeStep={activeStep} className="mb-6 mt-6">
        {stepLabels.map((label, index) => (
          <Step key={label}>
            <StepLabel
              onClick={() => handleStepClick(index)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        className={
          config.inlineTitle
            ? "flex justify-between"
            : "flex justify-between items-center"
        }
      >
        <Button disabled={activeStep === 0} onClick={handleBack} className="mr-4">
          Back
        </Button>
        <StandardWizardNextButton
          activeStep={activeStep}
          stepsLength={stepLabels.length}
          isLoading={isLoading}
          nextDisabled={nextDisabled}
          onNext={handleNext}
        />
      </Box>

      <Box className="mb-6">
        {activeStepConfig ? (
          <activeStepConfig.Component
            {...activeStepConfig.getProps(stepContext)}
          />
        ) : null}
      </Box>

      <Box
        className={
          config.inlineTitle
            ? "flex justify-between items-center"
            : "flex justify-between"
        }
      >
        <Button disabled={activeStep === 0} onClick={handleBack} className="mr-4">
          Back
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleSave}
          className="mx-2"
          endIcon={isSaving && <CircularProgress size={20} />}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <StandardWizardNextButton
          activeStep={activeStep}
          stepsLength={stepLabels.length}
          isLoading={isLoading}
          nextDisabled={nextDisabled}
          onNext={handleNext}
          testId="bond-wizard-next"
        />
      </Box>
    </Box>
  );
}

function BlanketBondWizardLayout({ config, form }) {
  const {
    activeStep,
    isLoading,
    isSaving,
    handleSubmit,
    onSubmit,
    handleNext,
    handleBack,
    handleSave,
  } = form;
  const isGuest = config.getIsGuest(form);
  const steps = config.blanketSteps;

  return (
    <Container maxWidth="md" sx={{ mt: 6, mb: 6, pb: isGuest ? 10 : 0 }}>
      {renderBondWizardHeader({ config, HeaderComponent: config.HeaderComponent, isGuest, form })}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper
        elevation={3}
        sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {config.renderBlanketStep(activeStep, form)}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleBack}
                sx={{ mr: 2 }}
                type="button"
              >
                Back
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSave}
              sx={{ mx: 2 }}
              endIcon={isSaving && <CircularProgress size={20} />}
              disabled={isSaving}
              type="button"
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                disabled={isLoading}
                endIcon={
                  isLoading && <CircularProgress size={20} color="inherit" />
                }
                type="submit"
                variant="contained"
                color="primary"
              >
                Submit
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                color="primary"
                data-testid="bond-wizard-next"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

const BondWizardPageShell = ({ config, form }) => {
  const isGuest = config.getIsGuest(form);
  const {
    supportModalOpen,
    setSupportModalOpen,
    submissionError,
    handleSupportRequestSubmit,
    openCalculator,
    setOpenCalculator,
    openSupport,
    setOpenSupport,
    isDialogOpen,
    setIsDialogOpen,
    isDuplicateDialogOpen,
    duplicateBondId,
    handleDuplicateProceed,
    handleDuplicateCancel,
  } = form;

  const handleEsignatureClose = () => setIsDialogOpen?.(false);

  return (
    <>
      {isGuest ? <GuestNavbar /> : <Navbar />}

      {config.layout === "blanket" ? (
        <BlanketBondWizardLayout config={config} form={form} />
      ) : (
        <StandardBondWizardLayout config={config} form={form} />
      )}

      {config.hasEsignatureDialog ? (
        <BondWizardEsignatureDialog
          open={Boolean(isDialogOpen)}
          onClose={handleEsignatureClose}
        />
      ) : null}

      <SupportRequestModal
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        onSubmit={handleSupportRequestSubmit}
        errorMessage={submissionError}
        onGoBack={config.supportOnGoBack?.(form)}
      />

      {config.hasDuplicateDialog ? (
        <BondWizardDuplicateDialog
          open={Boolean(isDuplicateDialogOpen)}
          message={config.duplicateMessage}
          duplicateBondId={duplicateBondId}
          updatePath={config.duplicateBondUpdatePath}
          onCancel={handleDuplicateCancel}
          onProceed={handleDuplicateProceed}
        />
      ) : null}

      {isGuest ? (
        <>
          <BondWizardGuestFooter
            onOpenSupport={() => setOpenSupport(true)}
            onOpenCalculator={() => setOpenCalculator(true)}
          />
          <BondWizardGuestOverlays
            supportContext={config.guestSupportContext}
            openSupport={openSupport}
            setOpenSupport={setOpenSupport}
            openCalculator={openCalculator}
            setOpenCalculator={setOpenCalculator}
          />
        </>
      ) : null}
    </>
  );
};

BondWizardPageShell.propTypes = {
  config: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
};

export default BondWizardPageShell;
